import React, { useEffect, useRef, useState } from 'react';

import Konva from 'konva';
import { Label, Tag, Text } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';

import TextEditor from 'Components/CanvasEditor/components/elements/TextEditor';

import type {
  ButtonElement as ButtonElementType,
  CanvasElement,
} from 'Components/CanvasEditor/types';

interface ButtonProps {
  element: ButtonElementType;
  onDragStart: (e: KonvaEventObject<DragEvent>) => void;
  onDragEnd: (e: KonvaEventObject<DragEvent>) => void;
  onElementChange: (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => void;
}

export default function ButtonElement({
  element,
  onElementChange,
  onDragStart,
  onDragEnd,
}: ButtonProps) {
  const textRef = useRef<Konva.Text>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [textValue, setTextValue] = useState(element.text);

  useEffect(() => {
    setTextValue(element.text);
  }, [element.text]);

  const handleDblClick = () => {
    setIsEditing(true);
  };

  const onClose = () => {
    setIsEditing(false);

    if (textValue !== element.text) {
      onElementChange({ text: textValue }, element.id, true);
    }
  };

  const onInput = (value: string) => {
    setTextValue(value);
  };

  const onTransform = (e: KonvaEventObject<Event>) => {
    const node = e.currentTarget;

    const scaleX = node.scaleX();

    const newWidth = node.width() * scaleX;

    node.scaleX(1);
    node.scaleY(1);

    textRef.current?.scaleX(1);
    textRef.current?.scaleY(1);

    onElementChange(
      {
        x: node.x(),
        y: node.y(),
        width: newWidth,
        scaleX: 1,
        scaleY: 1,
      },
      element.id
    );
  };

  const onTransformEnd = (e: KonvaEventObject<Event>) => {
    const node = e.currentTarget;

    const scaleX = node.scaleX();

    const newWidth = node.width() * scaleX;

    node.scaleX(1);
    node.scaleY(1);

    textRef.current?.scaleX(1);
    textRef.current?.scaleY(1);

    onElementChange(
      {
        x: node.x(),
        y: node.y(),
        width: newWidth,
        scaleX: 1,
        scaleY: 1,
      },
      element.id,
      true
    );
  };

  return (
    <Label
      id={element.id}
      name="object"
      x={element.x}
      y={element.y}
      draggable
      visible={element.visible}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onTransform={onTransform}
      onTransformEnd={onTransformEnd}
    >
      <Tag fill={element.background} cornerRadius={element.cornerRadius} />
      <Text
        ref={textRef}
        width={element.width}
        text={textValue}
        fontSize={element.fontSize}
        fontFamily={element.fontFamily}
        fontStyle={element.fontStyle}
        textDecoration={element.textDecoration}
        fill={element.fill}
        align={element.align}
        rotation={element.rotation}
        scaleX={element.scaleX}
        scaleY={element.scaleY}
        visible={!isEditing}
        onDblClick={handleDblClick}
        perfectDrawEnabled
        padding={10}
      />
      {isEditing && (
        <TextEditor
          textNode={textRef.current as Konva.Text}
          onChange={onElementChange}
          onInput={onInput}
          onClose={onClose}
        />
      )}
    </Label>
  );
}
